import { useState, useEffect, useCallback, useRef } from 'react';
import { HealthCheckService, SystemStatus } from '../services/healthCheckService';

interface UseSystemStatusOptions {
  checkInterval?: number;
  autoStart?: boolean;
}

export function useSystemStatus(options: UseSystemStatusOptions = {}) {
  const { checkInterval = 60000, autoStart = true } = options;

  const [status, setStatus] = useState<SystemStatus>({
    status: 'online',
    supabase: {
      status: 'operational',
      lastChecked: new Date()
    },
    internet: {
      status: 'operational',
      lastChecked: new Date()
    },
    lastChecked: new Date()
  });

  const [isChecking, setIsChecking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isCheckingRef = useRef(false);

  const checkHealth = useCallback(async () => {
    if (isCheckingRef.current) return;

    isCheckingRef.current = true;
    setIsChecking(true);
    setError(null);

    try {
      const healthStatus = await HealthCheckService.checkSystemHealth();
      setStatus(healthStatus);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to check system health';
      setError(errorMessage);

      setStatus(prev => ({
        ...prev,
        status: 'offline',
        lastChecked: new Date()
      }));
    } finally {
      isCheckingRef.current = false;
      setIsChecking(false);
    }
  }, []);

  useEffect(() => {
    if (!autoStart) return;

    let intervalId: NodeJS.Timeout;

    const performInitialCheck = async () => {
      await checkHealth();

      intervalId = setInterval(() => {
        checkHealth();
      }, checkInterval);
    };

    performInitialCheck();

    const handleOnline = () => {
      checkHealth();
    };

    const handleOffline = () => {
      setStatus(prev => ({
        ...prev,
        status: 'offline',
        internet: {
          status: 'down',
          error: 'Device is offline',
          lastChecked: new Date()
        },
        lastChecked: new Date()
      }));
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [checkInterval, autoStart, checkHealth]);

  const isOnline = status.status === 'online';
  const isDegraded = status.status === 'degraded';
  const isOffline = status.status === 'offline';

  return {
    status,
    isChecking,
    error,
    checkHealth,
    isOnline,
    isDegraded,
    isOffline
  };
}
